import { useState } from 'react';
import styles from '../../index.module.scss';
import type { SmartImportChat } from '../useSmartImportChat';

const PENDING_PREFIXES = ['__pending__', '__template_pending__'];
const isSentinel = (category: string) => category === 'employee_name' || category === 'skip';

export default function MappingCard({ chat }: { chat: SmartImportChat }) {
	const { session, dispatch, sections } = chat;
	const [saveOpen, setSaveOpen] = useState(false);
	const [templateName, setTemplateName] = useState('');
	const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

	const handleSave = async () => {
		const name = templateName.trim();
		if (!name) return;
		setSaveStatus('saving');
		const ok = await chat.saveMappingAsTemplate(name);
		setSaveStatus(ok ? 'saved' : 'error');
	};

	return (
		<div className={styles['action-card']}>
			<div className={styles['action-card__summary']}>
				Map each column to a category and field. Toggle Convert for any column whose sign needs flipping.
			</div>
			<div className={styles['import-table-wrap']}>
				<table className={styles['import-table']}>
					<thead>
						<tr>
							<th>File Column</th>
							<th>Sample</th>
							<th>Category</th>
							<th>Map to Field</th>
							<th>Convert</th>
						</tr>
					</thead>
					<tbody>
						{session.columnMappings.map((m) => {
							const section = sections.find((s) => s.id === m.category);
							const sectionPending = session.pendingFields.filter((pf) => pf.sectionId === m.category);
							const fieldValue =
								m.fieldRef.type === 'existing' ? m.fieldRef.fieldId : m.fieldRef.type === 'pending' ? m.fieldRef.tempId : '';
							return (
								<tr key={m.colIndex}>
									<td>
										<strong>{m.sourceHeader}</strong>
									</td>
									<td>{m.sampleValues.slice(0, 2).join(', ')}</td>
									<td>
										<select
											value={m.category}
											onChange={(e) =>
												dispatch({ type: 'COLUMN_CATEGORY_CHANGED', colIndex: m.colIndex, category: e.target.value })
											}
										>
											<option value="employee_name">Employee Name</option>
											{[...sections]
												.sort((a, b) => a.no - b.no)
												.map((s) => (
													<option key={s.id} value={s.id}>
														{s.name}
													</option>
												))}
											<option value="skip">Skip</option>
										</select>
									</td>
									<td>
										{isSentinel(m.category) ? (
											'—'
										) : (
											<select
												value={fieldValue}
												onChange={(e) => {
													const value = e.target.value;
													dispatch({
														type: 'COLUMN_FIELD_CHANGED',
														colIndex: m.colIndex,
														fieldRef: PENDING_PREFIXES.some((p) => value.startsWith(p))
															? { type: 'pending', tempId: value }
															: { type: 'existing', fieldId: value },
													});
												}}
											>
												<option value="" disabled>
													Select a field
												</option>
												{(section?.fields ?? [])
													.filter((f) => !f.isTotal)
													.map((f) => (
														<option key={f.id} value={f.id}>
															{f.name}
														</option>
													))}
												{sectionPending.map((pf) => (
													<option key={pf.tempId} value={pf.tempId}>
														{pf.label} (new)
													</option>
												))}
											</select>
										)}
									</td>
									<td style={{ textAlign: 'center' }}>
										{!isSentinel(m.category) && m.classifier === 'numeric' && (
											<button
												type="button"
												className={`${styles['import-table__toggle']} ${m.negate ? styles['import-table__toggle--on'] : ''}`}
												onClick={() => dispatch({ type: 'COLUMN_NEGATE_TOGGLED', colIndex: m.colIndex })}
											>
												{m.negate ? 'On' : 'Off'}
											</button>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{chat.mappingValidationError && (
				<div className={styles['action-card__status']}>{chat.mappingValidationError}</div>
			)}

			<div className={styles['smart-import-card__buttons-wrap']}>
				<button type="button" className={styles['action-card__cancel']} onClick={chat.reSuggestCategories}>
					Suggest categories
				</button>
				{!saveOpen ? (
					<button type="button" className={styles['action-card__cancel']} onClick={() => setSaveOpen(true)}>
						Save as template
					</button>
				) : (
					<div className={styles['smart-import-card__save-row']}>
						<input
							className={styles['smart-import-card__name-input']}
							value={templateName}
							onChange={(e) => setTemplateName(e.target.value)}
							placeholder="Template name"
							maxLength={100}
						/>
						<button
							type="button"
							className={styles['action-card__confirm']}
							disabled={!templateName.trim() || saveStatus === 'saving'}
							onClick={() => void handleSave()}
						>
							Save
						</button>
					</div>
				)}
				<button
					type="button"
					className={styles['action-card__confirm']}
					disabled={!!chat.mappingValidationError || chat.loading}
					onClick={() => void chat.continueFromMapping()}
				>
					{chat.loading ? 'Loading…' : 'Continue'}
				</button>
			</div>
			{saveStatus === 'saved' && <div className={styles['action-card__status']}>✓ Template saved</div>}
			{saveStatus === 'error' && <div className={styles['action-card__status']}>Could not save the template.</div>}
		</div>
	);
}
